<template>
	<div
		class="p-4 sm:p-6 lg:p-10 bg-white border border-gray-200 rounded-2xl sm:rounded-3xl shadow-card hover:shadow-xl transition-shadow duration-300 animate-fadeUp"
	>
		<div v-if="!showCoverOptions" class="mb-6 text-center sm:mb-8">
			<div
				class="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full sm:w-20 sm:h-20 ring-4 ring-blue-100 shadow-lg"
			>
				<i class="fas fa-question-circle text-white"></i>
			</div>
			<h2
				class="mb-2 text-2xl font-bold sm:text-3xl text-gray-900 font-display"
			>
				Vehicle Value
			</h2>
			<p
				class="max-w-2xl mx-auto text-sm leading-relaxed sm:text-base text-gray-600"
			>
				Enter your vehicle's current market value to see available
				coverage options
			</p>
		</div>

		<div v-else class="mb-6 text-center sm:mb-8">
			<h2
				class="mb-3 text-xl font-bold sm:text-3xl lg:text-4xl text-gray-900 font-display"
			>
				Choose Your Cover Type
			</h2>
			<p
				class="max-w-2xl mx-auto text-xs sm:text-base lg:text-lg text-gray-600 mb-3 sm:mb-4 px-2"
			>
				Select the type of insurance coverage you're looking for
			</p>
			<div
				class="mt-3 px-3 py-2 sm:px-4 sm:py-2.5 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200 inline-flex items-center shadow-sm"
			>
				<i
					class="fas fa-shield-alt text-blue-600 flex-shrink-0 mr-1.5 sm:mr-2"
				></i>
				<p class="text-xs sm:text-sm text-blue-700">
					Vehicle Value:
					<span class="font-bold"
						>KSH
						{{
							selectQuotationData
								? selectQuotationData.vehicleValue.toLocaleString()
								: '0'
						}}</span
					>
					<button
						@click="editVehicleValue"
						class="ml-1.5 sm:ml-2 text-xs text-blue-600 underline hover:text-blue-800 transition-colors font-medium"
					>
						Edit
					</button>
				</p>
			</div>
		</div>

		<div
			v-if="Object.keys(errors).length"
			class="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl animate-shake"
		>
			<div class="flex items-start">
				<i
					class="fas fa-info-circle text-red-500 mr-2 mt-0.5 flex-shrink-0"
				></i>
				<div>
					<p
						v-for="(error, key) in errors"
						:key="key"
						class="text-xs sm:text-sm"
					>
						{{ error }}
					</p>
				</div>
			</div>
		</div>

		<form
			v-if="!showCoverOptions"
			@submit.prevent="submitVehicleValue"
			class="max-w-md mx-auto space-y-6"
		>
			<div class="space-y-3">
				<label
					for="vehicleValue"
					class="flex items-center text-sm font-semibold text-gray-800 sm:text-base"
				>
					<i class="fas fa-car text-blue-600 mr-2"></i>
					Vehicle Value (KSH)
				</label>
				<div class="relative">
					<span
						class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-base font-medium pointer-events-none"
						>KSH</span
					>
					<input
						type="number"
						id="vehicleValue"
						v-model.number="vehicleValueInput"
						required
						min="100000"
						step="1000"
						placeholder="e.g. 1,500,000"
						class="w-full h-12 pl-16 pr-4 text-base text-gray-800 transition-all duration-200 border-2 border-gray-200 rounded-xl shadow-sm bg-white sm:h-14 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300"
					/>
				</div>
				<p class="text-xs text-gray-500 sm:text-sm">
					Enter the current market value of your vehicle
				</p>
			</div>

			<div
				class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200 shadow-sm"
			>
				<div class="flex items-start">
					<i
						class="fas fa-info-circle text-blue-600 mr-2 mt-0.5 flex-shrink-0"
					></i>
					<div>
						<h4 class="text-sm font-semibold text-blue-700 mb-1">
							Why do we need this?
						</h4>
						<p class="text-xs text-blue-600 leading-relaxed">
							Your vehicle's value helps us estimate the basic
							premium payable based on the proposed insurance
							value. This serves only as a guide, as individual
							insurance companies may apply specific conditions
							depending on the vehicle's year and model.
						</p>
					</div>
				</div>
			</div>

			<div
				class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between sm:items-center sm:gap-4 pt-4"
			>
				<button
					type="button"
					@click="previous"
					class="flex items-center justify-center h-12 sm:h-14 px-6 text-sm sm:text-base font-semibold border-2 rounded-full transition-all duration-300 w-full sm:w-auto focus:outline-none focus:ring-2 active:scale-95"
					style="
						color: rgb(37, 99, 235);
						border-color: rgb(37, 99, 235);
					"
					onmouseover="
						this.style.color = 'white';
						this.style.backgroundColor = 'rgb(37, 99, 235)';
					"
					onmouseout="
						this.style.color = 'rgb(37, 99, 235)';
						this.style.backgroundColor = 'transparent';
					"
					onfocus="
						this.style.outline = 'none';
						this.style.boxShadow =
							'0 0 0 3px rgba(37, 99, 235, 0.1)';
					"
					onblur="this.style.boxShadow = 'none'"
				>
					<i
						class="fas fa-arrow-left mr-2"
						style="color: inherit"
					></i>
					Back
				</button>

				<button
					type="submit"
					class="flex items-center justify-center h-12 sm:h-14 px-6 text-sm sm:text-base font-semibold text-white rounded-full shadow-lg transition-all duration-300 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-95"
				>
					Continue
					<i class="fas fa-arrow-right ml-2"></i>
				</button>
			</div>
		</form>

		<div v-else>
			<div
				v-if="selectedClass"
				class="grid max-w-6xl grid-cols-2 gap-3 mx-auto mb-6 sm:gap-6 md:grid-cols-2 lg:grid-cols-3"
			>
				<div
					v-for="cover in selectedClass.insurancecovertypes"
					:key="cover._id"
					@click="selectCover(cover)"
					:class="[
						'relative overflow-hidden transition-all duration-300 ease-out transform bg-white border-2 group rounded-xl sm:rounded-2xl p-3 sm:p-6 cursor-pointer',
						selectedCoverType.name === cover.name
							? 'border-blue-600 shadow-xl scale-[1.02] bg-blue-50'
							: 'border-gray-200 shadow-md hover:shadow-lg hover:border-blue-400 active:scale-95',
					]"
				>
					<div
						class="absolute top-2 right-2 sm:top-4 sm:right-4 z-10"
					>
						<div
							:class="[
								'w-5 h-5 sm:w-7 sm:h-7 rounded-full border-2 flex items-center justify-center transition-all duration-300',
								selectedCoverType.name === cover.name
									? 'bg-blue-600 border-blue-600 text-white shadow-lg scale-110'
									: 'border-gray-300 bg-white group-hover:border-blue-400',
							]"
						>
							<i
								v-if="selectedCoverType.name === cover.name"
								class="fas fa-check w-3 h-3 sm:w-4 sm:h-4"
							></i>
						</div>
					</div>

					<div class="relative z-10">
						<div
							:class="[
								'flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 mb-2 sm:mb-4 rounded-lg sm:rounded-xl transition-all duration-300',
								selectedCoverType.name === cover.name
									? 'bg-blue-600 shadow-lg'
									: 'bg-blue-100 group-hover:bg-blue-200',
							]"
						>
							<i
								:class="[
									'fas fa-shield-alt w-5 h-5 sm:w-7 sm:h-7 transition-colors duration-300',
									selectedCoverType.name === cover.name
										? 'text-white'
										: 'text-blue-600',
								]"
							></i>
						</div>

						<h4
							:class="[
								'mb-1 sm:mb-2 text-sm sm:text-lg lg:text-xl font-bold leading-tight transition-colors duration-300',
								selectedCoverType.name === cover.name
									? 'text-blue-700'
									: 'text-gray-900 group-hover:text-blue-600',
							]"
						>
							{{ cover.name }}
						</h4>

						<p
							:class="[
								'hidden md:block text-sm leading-relaxed mb-4 transition-colors duration-300',
								selectedCoverType.name === cover.name
									? 'text-gray-700'
									: 'text-gray-600 group-hover:text-gray-800',
							]"
						>
							{{ cover.description }}
						</p>

						<div
							v-if="getFeatures(cover.code).length"
							class="hidden lg:block space-y-2 pt-3 border-t border-gray-200"
						>
							<div
								v-for="feature in getFeatures(cover.code)"
								:key="feature"
								class="flex items-start text-xs sm:text-sm text-green-700"
							>
								<i
									class="fas fa-check text-green-700 mr-2 mt-0.5 flex-shrink-0"
								></i>
								<span class="leading-tight">{{ feature }}</span>
							</div>
						</div>

						<div
							:class="[
								'mt-2 sm:mt-4 pt-2 sm:pt-3 text-center text-xs sm:text-sm font-medium transition-all duration-300',
								selectedCoverType.name === cover.name
									? 'text-blue-700'
									: 'text-gray-500 group-hover:text-blue-600',
							]"
						>
							<span
								v-if="selectedCoverType.name === cover.name"
								class="flex items-center justify-center"
							>
								<i
									class="fas fa-check-circle w-3 h-3 sm:w-4 sm:h-4 mr-1"
								></i>
								Selected
							</span>
							<span v-else class="text-xs">Tap to select</span>
						</div>
					</div>
				</div>
			</div>

			<div class="flex justify-center mt-6 sm:mt-8">
				<button
					type="button"
					@click="editVehicleValue"
					class="flex items-center justify-center h-12 sm:h-14 px-6 text-sm sm:text-base font-semibold border-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 active:scale-95"
					style="
						color: rgb(37, 99, 235);
						border-color: rgb(37, 99, 235);
					"
					onmouseover="
						this.style.color = 'white';
						this.style.backgroundColor = 'rgb(37, 99, 235)';
					"
					onmouseout="
						this.style.color = 'rgb(37, 99, 235)';
						this.style.backgroundColor = 'transparent';
					"
					onfocus="
						this.style.outline = 'none';
						this.style.boxShadow =
							'0 0 0 3px rgba(37, 99, 235, 0.1)';
					"
					onblur="this.style.boxShadow = 'none'"
				>
					<i
						class="fas fa-arrow-left mr-2"
						style="color: inherit"
					></i>
					Back
				</button>
			</div>
		</div>

		<div
			v-if="isProcessing"
			class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
		>
			<div
				class="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl text-center max-w-sm w-full"
			>
				<div
					class="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-blue-600 mx-auto mb-4"
				></div>
				<p class="text-base sm:text-lg font-semibold text-gray-800">
					Processing your selection...
				</p>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { FetchClassRecommendations } from '../../../api/insurance';

export default {
	name: 'CoverTypeSelection',
	data() {
		return {
			vehicleValueInput: null,
			showCoverOptions: false,
			isProcessing: false,
		};
	},
	computed: {
		...mapState('insurance/motorPrivate', [
			'selectedCoverType',
			'errors',
			'vehicleValue',
		]),
		...mapGetters('product', [
			'selectedProduct',
			'selectedCoverType',
			'selectedClass',
		]),
		...mapGetters('quotation', ['selectQuotationData']),
		selectedProductData() {
			return this.selectedProduct;
		},
	},
	methods: {
		...mapMutations('insurance/motorPrivate', [
			'SET_SELECTED_COVER_TYPE',
			'SET_VEHICLE_VALUE',
		]),
		...mapActions('insurance/motorPrivate', [
			'setVehicleValue',
			'clearErrors',
			'setErrors',
		]),
		...mapActions('quotation', ['setQuotationData']),
		...mapActions('product', ['setCoverType', 'setInsuranceClass']),

		submitVehicleValue() {
			this.clearErrors();

			if (!this.vehicleValueInput) {
				this.setErrors({
					vehicleValue: 'Vehicle value is required.',
				});
				return;
			}

			if (this.vehicleValueInput < 50000) {
				this.setErrors({
					vehicleValue: 'Vehicle value must be at least KSH 50,000.',
				});
				return;
			}
			this.setQuotationData({ vehicleValue: this.vehicleValueInput });

			this.showCoverOptions = true;

			Event.$emit('notifications:show', {
				type: 'success',
				title: 'Vehicle Value Saved',
				message: `Vehicle value of KSH ${this.vehicleValueInput.toLocaleString()} has been saved.`,
				duration: 3000,
			});
		},

		editVehicleValue() {
			this.showCoverOptions = false;
			this.clearErrors();
		},

		async selectCover(cover) {
			this.clearErrors();

			this.$store.dispatch('product/setCoverType', cover);

			Event.$emit('notifications:show', {
				type: 'success',
				title: 'Cover Type Selected',
				message: `You have selected <strong>${cover.name}</strong>.`,
				duration: 2000,
			});

			setTimeout(() => {
				this.getQuote();
			}, 500);
		},

		previous() {
			this.clearErrors();
			this.$router.push({ name: 'insurance' });
		},

		quoteData() {
			if (!this.selectedClass || !this.selectedClass.ira_code) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Selection Required',
					message:
						'Insurance class code is required but not provided.',
					duration: 3000,
				});
			}

			return {
				insuranceClass: this.selectedClass.ira_code,
				riskValue: this.vehicleValueInput,
				coverType: this.selectedCoverType.system_covertype_ref,
			};
		},

		getFeatures(code) {
			if (code === 'TPO')
				return [
					'Third party liability coverage',
					'Legal representation included',
				];
			if (code === 'TPTF')
				return [
					'Third party liability protection',
					'Fire & theft coverage',
					'Loss damage protection',
				];
			if (code === 'COMP') {
				return [
					'Full comprehensive protection',
					'All-risk coverage',
					'Maximum peace of mind',
				];
			}
			return [];
		},

		async getQuote() {
			if (!this.selectedCoverType.name) {
				this.setErrors({
					selectedCoverType: 'Please select a cover type.',
				});

				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Selection Required',
					message: 'Please select a cover type to proceed.',
					duration: 3000,
				});
				return;
			}

			this.isProcessing = true;
			this.clearErrors();

			try {
				const res = await FetchClassRecommendations(this.quoteData());
				if (res.status) {
					this.setInsuranceClass(res.data.result.data);

					this.$router.push({
						name: 'ProductQuotation',
						params: this.$route.params,
					});
				} else {
					Event.$emit('notifications:show', {
						type: 'error',
						title: 'Quotation Fetch Failed',
						message: 'An error occurred while fetching quotation.',
						duration: 3000,
					});
				}
			} catch (error) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: 'An unexpected error occurred. Please try again.',
					duration: 3000,
				});
			} finally {
				this.isProcessing = false;
			}
		},
	},

	mounted() {
		this.clearErrors();

		if (this.vehicleValue && this.vehicleValue > 0) {
			this.vehicleValueInput = this.vehicleValue;
			this.showCoverOptions = true;
		}

		console.log('selected product:', this.selectedClass);
		console.log('selected covertype:', this.selectedCoverType);
	},
};
</script>
